import React from 'react';
import mixpanel from 'mixpanel-browser';
import { FormattedMessage } from 'react-intl';
import { Snackbar } from '../components/snackbar';
import {
    LOADING,
    COMPLETE,
    ONBOARDING_GET_JOB,
    ONBOARDING_GET_PROJECTS,
    ONBOARDING_GET_POSITION,
    ONBOARDING_GET_ADMIN,
    ONBOARDING_PROGRESSION_SET_PROPERTIES,
    ONBOARDING_PROGRESSION_ALL_DONE,
    ONBOARDING_PROGRESSION_INTRO_1_GET_SIGNATURE_OK,
    ONBOARDING_PROGRESSION_INTRO_1_SIGN_OK,
    ONBOARDING_PROGRESSION_INTRO_2_CHOOSE_CONTRACT_TYPE,
    ONBOARDING_GET_OFFER_LETTER_OK,
    ONBOARDING_GET_ASSIGNMENT_LETTER_OK,
    ONBOARDING_GET_ARBITRATION_AGREEMENT_OK,
    ONBOARDING_GET_CONTRACTOR_AGREEMENT_OK,
    SAVING
} from './types';
import { call } from './../utilities/http';
import { sign } from './signatures';
import { sortByString } from '../utilities/string';

const defaultState = {
    job: undefined,
    address: undefined,
    client: undefined,
    position: undefined,
    admin: undefined,
    classifications: undefined,
    questions: undefined,
    projects: undefined,
    offerLetter: undefined,
    assignmentLetter: undefined,
    arbitrationAgreement: undefined,
    contractorAgreement: undefined,
    progression: {
        agreementSigned: undefined
    }
};

/**
 * Loading state mapper.
 * @param {object} state
 * @param {string} action
 */
export default function(state = defaultState, action) {
    switch (action.type) {
        case ONBOARDING_GET_JOB:
            const { job, client, address, hiring_manager } = action.data;
            return { ...state, job, client, address, hiring_manager };

        case ONBOARDING_GET_PROJECTS:
            return { ...state, projects: action.data };

        case ONBOARDING_GET_POSITION:
            return { ...state, position: action.data };

        case ONBOARDING_GET_ADMIN:
            if (action && action.data) {
                const { id, name } = action.data;
                mixpanel.people.set({ admin_id: id, admin_name: name });
            }
            return { ...state, admin: action.data };

        // onboarding progression
        case ONBOARDING_PROGRESSION_SET_PROPERTIES:
            return { ...state, progression: { ...state.progression, ...action.data } };

        case ONBOARDING_PROGRESSION_INTRO_1_SIGN_OK:
        case ONBOARDING_PROGRESSION_INTRO_1_GET_SIGNATURE_OK:
            return { ...state, progression: { ...state.progression, agreementSigned: action.data } };

        case ONBOARDING_PROGRESSION_INTRO_2_CHOOSE_CONTRACT_TYPE:
            return { ...state, progression: { ...state.progression, contractType: action.data } };

        case ONBOARDING_PROGRESSION_ALL_DONE:
            return defaultState;

        case ONBOARDING_GET_OFFER_LETTER_OK:
            return { ...state, offerLetter: action.data };

        case ONBOARDING_GET_ASSIGNMENT_LETTER_OK:
            return { ...state, assignmentLetter: action.data };

        case ONBOARDING_GET_ARBITRATION_AGREEMENT_OK:
            return { ...state, arbitrationAgreement: action.data };

        case ONBOARDING_GET_CONTRACTOR_AGREEMENT_OK:
            return { ...state, contractorAgreement: action.data };

        default:
            return state;
    }
}

export function getJob(id, onSuccess = response => null) {
    let errorMessage = <FormattedMessage id={'error.getJob'} defaultMessage={'Unable to retrieve job.'} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/job/${id}?client_info=true` })
            .then(response => {
                dispatch({ type: ONBOARDING_GET_JOB, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getProjects(jobId, onSuccess = response => null) {
    let errorMessage = <FormattedMessage id={'error.getProjects'} defaultMessage={'Unable to retrieve job Projects.'} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/job/${jobId}/projects` })
            .then(response => {
                const sortedResponse = response && sortByString(response, 'name');
                dispatch({ type: ONBOARDING_GET_PROJECTS, data: sortedResponse });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPosition(id, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/position/${id}?extended=true` })
            .then(response => {
                dispatch({ type: ONBOARDING_GET_POSITION, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getPosition'} defaultMessage={'Unable to retrieve position.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getAdmin(id, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/admin/${id}` })
            .then(response => {
                dispatch({ type: ONBOARDING_GET_ADMIN, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getAdmin'} defaultMessage={'Unable to retrieve onboarding.tenant info.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

// ------------------------------------------------
// Step progression actions - Common
// ------------------------------------------------

export function setProgressionProperties(data) {
    return async function(dispatch) {
        dispatch({ type: ONBOARDING_PROGRESSION_SET_PROPERTIES, data: data });
    };
}

export function completeOnboarding(onSuccess = () => null) {
    return async function(dispatch) {
        dispatch({ type: ONBOARDING_PROGRESSION_ALL_DONE });
        onSuccess();
    };
}

// ------------------------------------------------
// Step progression actions - Intro - Step 1
// ------------------------------------------------

export function getOfferLetter(jobId, output_ref_id, output_ref_type, doc_type, onSuccess = () => null, onError = () => null) {
    return getJobDocument(jobId, output_ref_id, output_ref_type, doc_type, ONBOARDING_GET_OFFER_LETTER_OK, onSuccess, onError);
}

export function getAssignmentLetter(jobId, output_ref_id, output_ref_type, doc_type, onSuccess = () => null, onError = () => null) {
    return getJobDocument(jobId, output_ref_id, output_ref_type, doc_type, ONBOARDING_GET_ASSIGNMENT_LETTER_OK, onSuccess, onError);
}

export function getArbitrationAgreement(jobId, output_ref_id, output_ref_type, doc_type, onSuccess = () => null, onError = () => null) {
    return getJobDocument(jobId, output_ref_id, output_ref_type, doc_type, ONBOARDING_GET_ARBITRATION_AGREEMENT_OK, onSuccess, onError);
}

function getJobDocument(jobId, output_ref_id, output_ref_type, doc_type, actionType, onSuccess = () => null, onError = () => null) {
    const queryParams = {
        doc_type,
        output_ref_id,
        output_ref_type
    };
    return async function(dispatch) {
        call({ path: `/job/${jobId}/doc_template`, method: 'GET', queryParams })
            .then(response => {
                dispatch({ type: actionType, data: response });
                onSuccess();
            })
            .catch(error => {
                onError(error);
            });
    };
}

export function getAgreementSignature(jobId, role, onSuccess = response => null) {
    const { id, type } = role;
    let errorMessage = <FormattedMessage id={'error.getAgreementSignature'} defaultMessage={'Unable to retrieve policy agreement signature.'} />;

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/esign?ref_type=${type}&ref_id=${id}` })
            .then(response => {
                let signature = response.find(r => r.name === `JobAgreeBegin:${jobId}`);
                dispatch({ type: ONBOARDING_PROGRESSION_INTRO_1_GET_SIGNATURE_OK, data: signature !== undefined });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function signAgreement(jobId, role, onSuccess = response => null) {
    return sign(role, `JobAgreeBegin:${jobId}`, (response, dispatch) => {
        dispatch({ type: ONBOARDING_PROGRESSION_INTRO_1_GET_SIGNATURE_OK, data: true });
        onSuccess(response);
    });
}
// ------------------------------------------------
// Step progression actions - Intro - Step 2
// ------------------------------------------------

export function preferContractType(job, type, onSuccess = response => null) {
    let errorMessage = <FormattedMessage id={'error.preferContractType'} defaultMessage={'Unable to persist contract preference.'} />;

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: `/job/${job.id}/action/prefer_${type}`, method: 'POST' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function submitICNotify(job, onSuccess = response => null) {
    let errorMessage = <FormattedMessage id={'error.submitICNotify'} defaultMessage={'Unable to notify of IC document submission.'} />;

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: `/job/${job.id}/action/submitICNotify`, method: 'POST' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function witnessReject(role, onSuccess = response => null) {
    let errorMessage = <FormattedMessage id={'error.witnessReject'} defaultMessage={'Unable to persist choice.'} />;
    const { id, type } = role;

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: `/${type}/${id}/action/witness_reject`, method: 'POST' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}



// WEBPACK FOOTER //
// ./src/reducers/onboarding.jsx